import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeToShoppingList,
  addShoppingItem,
  updateShoppingItem,
  deleteShoppingItem,
  batchDeleteShoppingItems,
} from '../services/firebase';

// 買い物リスト（全画面ページ表示）
// - 既存タスク管理とは独立
// - ログイン中：Firestore (users/{uid}/shoppingList) でスマホ⇔PC リアルタイム同期
// - 未ログイン：localStorage（オフライン用フォールバック）
// - v1.8.0でモーダル表示からアイコンナビのページ表示へ変更（データ・ロジックは不変）

function getStorageKey(uid) {
  return `shoppingList:${uid || 'anonymous'}`;
}

function loadLocalItems(uid) {
  try {
    const raw = localStorage.getItem(getStorageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error('ShoppingList load failed:', err);
    return [];
  }
}

function saveLocalItems(uid, items) {
  try {
    localStorage.setItem(getStorageKey(uid), JSON.stringify(items));
  } catch (err) {
    console.error('ShoppingList save failed:', err);
  }
}

// Firestore Timestamp / number / undefined を比較可能な数値へ
function toMillis(v) {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return 0;
}

export default function ShoppingList() {
  const { user } = useAuth();
  const uid = user ? user.uid : null;
  const isCloud = !!user; // ログイン中は Firestore、未ログインは localStorage
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState('');
  const [hydrated, setHydrated] = useState(false);

  // Firestore リアルタイム同期 / localStorage 読み込み
  useEffect(() => {
    setHydrated(false);
    if (isCloud) {
      // ログイン中：Firestore リアルタイム購読
      const unsub = subscribeToShoppingList(uid, (data) => {
        setItems(data);
        setHydrated(true);
      });
      return () => unsub && unsub();
    } else {
      // 未ログイン：localStorage 読み込み
      setItems(loadLocalItems(uid));
      setHydrated(true);
      return undefined;
    }
  }, [uid, isCloud]);

  // 未ログイン時のみ localStorage に永続化（ログイン中は Firestore 側で永続化）
  useEffect(() => {
    if (!hydrated) return;
    if (isCloud) return;
    saveLocalItems(uid, items);
  }, [items, uid, hydrated, isCloud]);

  // 入力欄 focus 時、見える位置にスクロール（キーボード出現時の保険）
  const handleInputFocus = useCallback((e) => {
    const target = e.target;
    // キーボードが出るのを少し待ってからスクロール
    setTimeout(() => {
      try {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) {
        /* no-op */
      }
    }, 250);
  }, []);

  const handleAdd = useCallback(
    async (e) => {
      e.preventDefault();
      const name = newName.trim();
      if (!name) return;
      setNewName('');

      if (isCloud) {
        try {
          await addShoppingItem(uid, name);
        } catch (err) {
          console.error('Add shopping item failed:', err);
        }
      } else {
        const item = {
          id: 'shop_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
          name,
          bought: false,
          createdAt: Date.now(),
        };
        setItems((prev) => [...prev, item]);
      }
    },
    [newName, isCloud, uid]
  );

  const toggleBought = async (id) => {
    const target = items.find((it) => it.id === id);
    if (!target) return;
    const next = !target.bought;

    if (isCloud) {
      // オプティミスティック更新（リスナーで再同期される）
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, bought: next } : it)));
      try {
        await updateShoppingItem(uid, id, { bought: next });
      } catch (err) {
        console.error('Update shopping item failed:', err);
      }
    } else {
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, bought: next } : it)));
    }
  };

  const removeItem = async (id) => {
    if (isCloud) {
      setItems((prev) => prev.filter((it) => it.id !== id));
      try {
        await deleteShoppingItem(uid, id);
      } catch (err) {
        console.error('Delete shopping item failed:', err);
      }
    } else {
      setItems((prev) => prev.filter((it) => it.id !== id));
    }
  };

  const clearBought = async () => {
    const targetIds = items.filter((it) => it.bought).map((it) => it.id);
    if (targetIds.length === 0) return;

    if (isCloud) {
      setItems((prev) => prev.filter((it) => !it.bought));
      try {
        await batchDeleteShoppingItems(uid, targetIds);
      } catch (err) {
        console.error('Clear bought items failed:', err);
      }
    } else {
      setItems((prev) => prev.filter((it) => !it.bought));
    }
  };

  // 表示用：createdAt 昇順（クラウド側は serverTimestamp、ローカル側は number）
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt));
  }, [items]);

  const totalCount = sortedItems.length;
  const boughtCount = sortedItems.filter((it) => it.bought).length;

  return (
    <div className="task-column column-blue shopping-view">
      <div className="column-header">
        <div className="column-title-row">
          <span className="column-icon">🛒</span>
          <h2 className="column-title">買い物リスト</h2>
        </div>
        {totalCount > 0 && (
          <span className="column-count">
            {boughtCount}/{totalCount}
          </span>
        )}
      </div>

      <div className="shopping-page-body">
        {totalCount > 0 && (
          <div className="column-progress">
            <div
              className="column-progress-bar shopping-progress-bar"
              style={{
                width: `${totalCount > 0 ? (boughtCount / totalCount) * 100 : 0}%`,
              }}
            />
          </div>
        )}

          <form className="task-add-form" onSubmit={handleAdd}>
            <input
              type="text"
              className="task-add-input"
              placeholder="買うものを追加..."
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onFocus={handleInputFocus}
              maxLength={120}
            />
            <button
              type="submit"
              className="task-add-btn"
              disabled={!newName.trim()}
              aria-label="追加"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </form>

          <div className="shopping-list-items">
            {sortedItems.map((it) => (
              <div
                key={it.id}
                className={`shopping-item ${it.bought ? 'is-bought' : ''}`}
              >
                <label className="shopping-item-label">
                  <input
                    type="checkbox"
                    checked={!!it.bought}
                    onChange={() => toggleBought(it.id)}
                  />
                  <span className="shopping-item-name">{it.name}</span>
                </label>
                <button
                  type="button"
                  className="shopping-item-delete"
                  onClick={() => removeItem(it.id)}
                  aria-label="削除"
                  title="削除"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                  </svg>
                </button>
              </div>
            ))}

            {sortedItems.length === 0 && (
              <div className="empty-state">
                <span className="empty-icon">🛍️</span>
                <p>買い物リストは空です</p>
              </div>
            )}
          </div>

          {boughtCount > 0 && (
            <div className="shopping-list-actions">
              <button
                type="button"
                className="shopping-clear-btn"
                onClick={clearBought}
              >
                買ったものを削除（{boughtCount}件）
              </button>
            </div>
          )}
      </div>
    </div>
  );
}
